import axios from '../axiosInstance';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

/**
 * CANsense Dashboard API Client
 * Handles dashboard statistics and client overview data
 */

// ============================================================================
// Types
// ============================================================================

export interface DashboardStats {
  total: number;
  online: number;
  offline: number;
  warning: number;
  activeAlerts: number;
  websites: {
    total: number;
    up: number;
    down: number;
  };
}

export interface ClientStats {
  customerId: number;
  customerName: string;
  total: number;
  online: number;
  offline: number;
  warning: number;
  healthPercent: number;
  activeAlerts: number;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Helper function to determine API URL based on environment
 */
const getApiUrl = (endpoint: string): string => {
  return `${getApiBaseUrl()}/sense/dashboard${endpoint}`;
};

// ============================================================================
// Dashboard Service
// ============================================================================

/**
 * Dashboard Service class - handles all dashboard-related API calls
 */
export class DashboardService {
  /**
   * Get overall dashboard statistics
   * Route: GET /api3.2/sense/dashboard/stats
   */
  async getStats(): Promise<DashboardStats> {
    const API_URL = getApiUrl('/stats');
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  /**
   * Get per-client statistics for dashboard cards
   * Route: GET /api3.2/sense/dashboard/clients
   */
  async getClients(): Promise<ClientStats[]> {
    const API_URL = getApiUrl('/clients');
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const unwrapped = response.data?.data || response.data;
    return unwrapped?.clients || unwrapped;
  }

}

// Export singleton instance
export const dashboardService = new DashboardService();

// Export with original name for backward compatibility
export const dashboardApi = dashboardService;
